/**
 * Sent sections list – shows one section at a time (Sent, Scheduled, or Killed).
 * Section is selected via sub-tabs in the header.
 */

import type { CSSProperties, FC } from 'react';
import { AnimatePresence } from 'framer-motion';
import { CheckSquare, Square } from 'lucide-react';

import { Button } from '@/components/ui';
import { cn } from '@/lib/utils';
import { ColumnHeadersRow } from '../header';
import { PreparedDropsDraftsListRow } from '../rows';
import type { CatalogResolvers } from '../../shared/compositeCatalogLookups';
import { money } from '../../shared/utils';
import type { SentSection } from '../../shared/types';
import type { PreparedDrop } from '@/features/smswiser-campaigns/types/workspace.types';

const SECTION_LABELS: Record<SentSection, string> = {
  sent: 'Sent',
  scheduled: 'Scheduled',
  killed: 'Killed',
};

const EMPTY_LABELS: Record<SentSection, string> = {
  sent: 'No Sent drops',
  scheduled: 'No Scheduled drops',
  killed: 'No Killed drops',
};

interface SentSectionsListProps {
  sentBySection: { sent: PreparedDrop[]; scheduled: PreparedDrop[]; killed: PreparedDrop[] };
  sentSection: SentSection;
  highlightPreparedDropId?: string | null;
  sortBy?: string | null;
  sortDir?: 'asc' | 'desc';
  onSort?: (key: import('../../shared/types').SortKey) => void;
  selectedSet: Set<string>;
  onToggleSelected: (id: string, checked: boolean) => void;
  onToggleSectionAll: (section: SentSection) => void;
  catalog: CatalogResolvers;
  tagsForDrop: (drop: PreparedDrop) => string[] | undefined;
  tagColors?: Record<string, string>;
  showCountryColumn?: boolean;
  rowGridStyle?: CSSProperties;
  isBusy: boolean;
  sendingDropId?: string | null;
  killingDropIds?: string[];
  returnTo?: string;
  onSend: (id: string) => void;
  onEdit: (id: string | null) => void;
  onEditSent?: (drop: PreparedDrop) => void | Promise<void>;
}

export const SentSectionsList: FC<SentSectionsListProps> = ({
  sentBySection,
  sentSection,
  highlightPreparedDropId,
  sortBy,
  sortDir,
  onSort,
  selectedSet,
  onToggleSelected,
  onToggleSectionAll,
  catalog,
  tagsForDrop,
  tagColors,
  showCountryColumn = false,
  rowGridStyle,
  isBusy,
  sendingDropId,
  killingDropIds = [],
  returnTo,
  onSend,
  onEdit,
  onEditSent,
}) => {
  const drops = sentBySection[sentSection];
  const count = drops.length;
  const sectionIds = drops.map((d) => d.id);
  const allSelectedInSection =
    count > 0 && sectionIds.every((sid) => selectedSet.has(sid));
  const label = SECTION_LABELS[sentSection];
  const emptyLabel = EMPTY_LABELS[sentSection];

  return (
    <section className="flex flex-col">
      <div className="sticky top-0 z-10 flex flex-wrap items-center justify-between gap-2 border-b border-border/30 bg-muted/5 px-4 py-2">
        <h3 className="text-sm font-medium text-foreground">
          {label} ({count})
        </h3>
        <div className="flex flex-wrap items-center gap-2">
          {count > 0 && (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="gap-1.5 text-xs"
              onClick={() => onToggleSectionAll(sentSection)}
              aria-label={`Select all ${label.toLowerCase()}`}
            >
              {allSelectedInSection ? (
                <CheckSquare className="h-3.5 w-3.5" />
              ) : (
                <Square className="h-3.5 w-3.5" />
              )}
              Select all
            </Button>
          )}
        </div>
      </div>
      <ColumnHeadersRow
        activeTab="sent"
        sentSection={sentSection}
        sortBy={sortBy as import('../../shared/types').SortKey | null}
        sortDir={sortDir}
        onSort={onSort}
        showCountryColumn={showCountryColumn}
      />
      {count === 0 ? (
        <div className="px-4 py-3 text-sm text-muted-foreground">
          {emptyLabel}
        </div>
      ) : (
        <div className="divide-y divide-border/50">
          <AnimatePresence mode="popLayout">
            {drops.map((drop) => {
              const price = catalog.gatewayPricePerSms(drop);
              const costPerSms = price > 0 ? money(price) : null;
              const totalCost = price > 0 ? money(drop.amount * price) : null;
              return (
                <div
                  key={drop.id}
                  data-prepared-drop-id={drop.id}
                  className={cn(
                    highlightPreparedDropId === drop.id && 'ring-2 ring-inset ring-primary/50',
                  )}
                >
                  <PreparedDropsDraftsListRow
                    drop={drop}
                    costPerSms={costPerSms}
                    totalCost={totalCost}
                    isSelected={selectedSet.has(drop.id)}
                    isBusy={isBusy}
                    isSending={sendingDropId === drop.id}
                    isKilling={killingDropIds.includes(drop.id)}
                    gatewayName={catalog.gatewayName(drop) || `#${drop.routeId}`}
                    leadsListName={catalog.leadsListName(drop) || `#${drop.leadsListWiserId}`}
                    leadsListTags={tagsForDrop(drop)}
                    tagColors={tagColors}
                    showCountryColumn={showCountryColumn}
                    rowGridStyle={rowGridStyle}
                    returnTo={returnTo}
                    onToggleSelected={onToggleSelected}
                    onEdit={onEdit}
                    onSend={onSend}
                    onEditSent={onEditSent}
                  />
                </div>
              );
            })}
          </AnimatePresence>
        </div>
      )}
    </section>
  );
};
